"use client";

import { memo, useState, useRef, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { cn, formatDuration } from "@/lib/utils";
import type { TranscriptSegment } from "@/lib/types/domain";

interface EditableSegmentRowProps {
  segment: TranscriptSegment;
  /**
   * Called once with the edited text when the user commits (Enter/blur).
   * Escape exits without calling it, so the segment genuinely reverts.
   */
  onTextChange?: (segmentId: string, text: string) => void;
}

export const EditableSegmentRow = memo(function EditableSegmentRow({
  segment,
  onTextChange,
}: EditableSegmentRowProps) {
  const t = useTranslations("Transcript");
  const [isEditing, setIsEditing] = useState(false);
  // editText is only meaningful while editing; initialized on enter
  const [editText, setEditText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  // Focus + select on entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
      resizeTextarea();
    }
  }, [isEditing, resizeTextarea]);

  const enterEditMode = useCallback(() => {
    setEditText(segment.text);
    setIsEditing(true);
  }, [segment.text]);

  const exitWithSave = useCallback(() => {
    setIsEditing(false);
    if (editText !== segment.text) {
      onTextChange?.(segment.id, editText);
    }
  }, [editText, segment.id, segment.text, onTextChange]);

  const exitWithRevert = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter/Escape during IME composition confirm/cancel the candidate,
      // not the edit — committing here would exit with half-composed text.
      // Safari fires the confirming keydown after compositionend with
      // isComposing already false; it still carries legacy keyCode 229.
      if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) {
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        exitWithSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        exitWithRevert();
      }
    },
    [exitWithSave, exitWithRevert],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setEditText(e.target.value);
      resizeTextarea();
    },
    [resizeTextarea],
  );

  const canEdit = !!onTextChange;

  const handleSpanKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        enterEditMode();
      }
    },
    [enterEditMode],
  );

  return (
    <div className="flex gap-3.5 px-[18px] py-[9px] transition-colors hover:bg-accent">
      {segment.startMs != null && (
        <span className="min-w-[44px] whitespace-nowrap pt-[3px] text-right font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {formatDuration(segment.startMs)}
        </span>
      )}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={editText}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={exitWithSave}
            rows={1}
            className="w-full resize-none overflow-hidden border-b-2 border-primary bg-transparent py-0 text-[14.5px] leading-[1.65] outline-none"
          />
        ) : (
          <span
            className={cn(
              "text-[14.5px] leading-[1.65]",
              canEdit && "-mx-[3px] cursor-text rounded px-[3px] ring-1 ring-transparent transition-shadow hover:ring-input",
            )}
            role={canEdit ? "button" : undefined}
            tabIndex={canEdit ? 0 : undefined}
            aria-label={canEdit ? t("segments.editSegment") : undefined}
            onClick={canEdit ? enterEditMode : undefined}
            onKeyDown={canEdit ? handleSpanKeyDown : undefined}
          >
            {segment.text}
          </span>
        )}
      </div>
    </div>
  );
});
